import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';

import { Separator } from "../Headers/Separator";
import Carousel from 'react-multi-carousel';
import { CardAlumno } from "./CardAlumno";
import { getDocs, collection, query, orderBy } from 'firebase/firestore/lite';
import { db } from '../../firebase';

import 'react-multi-carousel/lib/styles.css';
import '../../styles/alumnos.css'



const responsive = {
    superLargeDesktop: {
      breakpoint: { max: 4000, min: 3000 },
      items: 5
    },
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 3
    },
    tablet: {
      breakpoint: { max: 1024, min: 464 },
      items: 2
    },
    mobile: {
      breakpoint: { max: 464, min: 0 },
      items: 1
    }
  };




export const Alumnos = ( props ) =>{
    const [researchLines, setResearchLines] = useState([]);

    // Cargar líneas de investigación desde Firebase para obtener el orden
    useEffect(() => {
        const fetchResearchLines = async () => {
            try {
                // Cargar todas las líneas ordenadas por 'order', luego filtrar por enabled
                const q = query(collection(db, "research-lines"), orderBy('order'));
                const querySnapshot = await getDocs(q);
                const newData = querySnapshot.docs
                    .map((doc) => ({ ...doc.data(), id:doc.id }))
                    .filter(line => line.enabled !== false); // Filtrar solo las deshabilitadas explícitamente
                setResearchLines(newData);
            } catch (error) {
                console.error('Error fetching research lines:', error);
                setResearchLines([]);
            }
        };
        fetchResearchLines();
    }, []);

    if ( !props.students || props.students.length <= 0 )
    {
        return null;
    }

    // Filter out graduated students (only show where graduados === false)
    const nonGraduatedStudents = props.students.filter(student => {
        // Default to false if graduados is undefined/null
        return student.graduados === false || student.graduados === undefined || student.graduados === null;
    });

    if (nonGraduatedStudents.length <= 0) {
        return null;
    }

    // Función para obtener el nombre de una línea de investigación por ID o nombre
    const getLineName = (lineaIdOrName) => {
        if (!lineaIdOrName || !researchLines.length) {
            return lineaIdOrName || 'Sin línea';
        }
        // Primero intentar buscar por ID
        let linea = researchLines.find(l => l.id === lineaIdOrName);
        // Si no se encuentra por ID, buscar por nombre (compatibilidad con datos antiguos)
        if (!linea) {
            linea = researchLines.find(l => 
                l.name?.trim().toLowerCase() === lineaIdOrName?.trim().toLowerCase()
            );
        }
        return linea ? linea.name : lineaIdOrName;
    };

    // Función para obtener el orden de una línea de investigación
    const getLineOrder = (lineaIdOrName) => {
        if (!lineaIdOrName || !researchLines.length) {
            return 999;
        }
        // Primero intentar buscar por ID
        let linea = researchLines.find(l => l.id === lineaIdOrName);
        // Si no se encuentra por ID, buscar por nombre (compatibilidad con datos antiguos)
        if (!linea) {
            linea = researchLines.find(l => 
                l.name?.trim().toLowerCase() === lineaIdOrName?.trim().toLowerCase()
            );
        }
        return linea?.order ?? 999;
    };

    // Group students by linea_investigacion (ahora puede ser ID o nombre)
    const groupByLinea = (students) => {
        const groups = {};
        students.forEach(student => {
            // Usar el ID o nombre como clave (normalizar a string)
            const lineaKey = student.linea_investigacion || 'sin línea';
            if (!groups[lineaKey]) {
                groups[lineaKey] = [];
            }
            groups[lineaKey].push(student);
        });
        return groups;
    };

    // Combine groups with less than 3 students
    const organizeGroups = (groups) => {
        const MIN_STUDENTS_PER_GROUP = 3;
        const smallGroups = [];
        const largeGroups = [];

        // Separar grupos grandes y pequeños, manteniendo información del orden
        Object.keys(groups).forEach(lineaKey => {
            const students = groups[lineaKey];
            const lineaIdOrName = students[0]?.linea_investigacion || lineaKey;
            const lineaName = getLineName(lineaIdOrName); // Convertir ID a nombre si es necesario
            const order = getLineOrder(lineaIdOrName);
            
            if (students.length >= MIN_STUDENTS_PER_GROUP) {
                largeGroups.push({
                    lineas: [lineaName],
                    students: students,
                    displayName: lineaName,
                    order: order
                });
            } else {
                smallGroups.push({
                    linea: lineaName,
                    students: students,
                    order: order
                });
            }
        });

        // Ordenar grupos pequeños por orden antes de combinarlos
        smallGroups.sort((a, b) => a.order - b.order);

        // Ordenar grupos grandes por el campo order (respeta el orden configurado)
        largeGroups.sort((a, b) => {
            const orderA = a.order ?? 999;
            const orderB = b.order ?? 999;
            return orderA - orderB;
        });

        // Combine small groups into one carousel (siempre al final)
        let combinedGroup = null;
        if (smallGroups.length > 0) {
            const combinedStudents = [];
            const combinedLineas = [];
            
            smallGroups.forEach(group => {
                combinedStudents.push(...group.students);
                combinedLineas.push(group.linea); // Ya es el nombre, no el ID
            });

            combinedGroup = {
                lineas: combinedLineas,
                students: combinedStudents,
                displayName: combinedLineas.length === 1 
                    ? combinedLineas[0] 
                    : combinedLineas.join(' / '),
                order: 9999 // Orden muy alto para que siempre esté al final
            };
        }

        // Retornar grupos grandes ordenados primero, luego el grupo combinado al final
        if (combinedGroup) {
            return [...largeGroups, combinedGroup];
        }

        return largeGroups;
    };

    // Process students: group by linea and combine small groups
    // Solo procesar si tenemos las líneas de investigación cargadas o si no hay líneas configuradas
    const groups = groupByLinea(nonGraduatedStudents);
    const groupedStudents = organizeGroups(groups);
    
    // Debug: Log para verificar el orden
    if (groupedStudents.length > 0) {
        console.log('Grupos ordenados:', groupedStudents.map(g => ({ name: g.displayName, order: g.order })));
        console.log('Research lines cargadas:', researchLines.map(l => ({ name: l.name, order: l.order })));
    }

    return(
        <section id="Estudiantes" className="section">
            <Separator name ='Grupo'/>

            <div className="container">
                {
                    groupedStudents.map((group, groupIdx) => (
                        <div key={groupIdx} className="mb-5" style={{marginBottom: '4rem'}}>
                            <h5 className="text-center mb-4" style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#192a56', marginBottom: '2rem'}}>
                                {group.displayName}
                            </h5>
                            <Carousel 
                                responsive={responsive}
                                showDots={true}
                                containerClass="carousel-container"
                            > 
                                {
                                    group.students.map((student, idx) => (
                                        <CardAlumno 
                                            idx={idx} 
                                            key={student.id || idx}
                                            student={student} 
                                            {...props} 
                                            isHomeCard={true}
                                        />
                                    ))
                                }
                            </Carousel>
                        </div>
                    ))
                }
            </div>


        </section>
    )
}

Alumnos.propTypes = {
    students: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        url_imagen: PropTypes.string,
        foto: PropTypes.string,
        carrera: PropTypes.string,
        linea_investigacion: PropTypes.string,
        icon_inv: PropTypes.string,
        proyecto: PropTypes.array,
    })),
    projectTypes: PropTypes.array,
};